/**
 * Projection for `SitesMapScene` — the locator map and the site heat table.
 *
 * Four demo fragments meet here. `mapSVG()` (line 2034) draws the six pins on
 * the Australia outline; `siteHeat()` (line 3151) is the band-distribution
 * table beside it. Both read the same six sites, so this file derives them
 * together and the scene renders one list. `renderOverview()` (line 2111) then
 * adds the six site cards (`.sitegrid`, line 2115) and the eight summary
 * measures (`.kpis.eight`, line 2123), which are derived here too.
 *
 * The map, the heat table and everything on a site card except availability
 * read eager data. Availability, emissions, the drafted orders and the data
 * store counts need lazy domains, so those functions take a context and the
 * scene renders a skeleton in their place until it arrives.
 *
 * Pure: no React, no fetching.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/sites/SitesMapScene.tsx
 * @see docs/logic/sites/sites-map.md
 */

import { FLEET, MINES, MINE_ORDER, WORKINGS } from '../../data/core';
import {
  bandOf,
  CONDITION_BAND_LABEL,
  CONDITION_BAND_ORDER,
  type ConditionBand,
  type ConditionBandSpec,
  type MachineClass,
  type MineCode,
} from '../../schema/machine';
import type { SourceCalc } from '../../chrome-oem/SourceDrawerBody';
import type { EventDailyRow } from '../../schema/telemetry';
import { unpackTable } from '../../shared/columnar';
import {
  availOf,
  avgAvail,
  bandsBestFirst,
  dataModeLabel,
  draftWorkOrders,
  groupNum,
  inWk,
  miningLabel,
  siteAvail,
  siteHeat,
  statusOf,
  type ShiftCtx,
  type SiteHeatRow,
  type WorkOrderCtx,
  WK,
} from '../../shared/oem-engine';
import { OEM_STATIC_COUNTS } from '../../shared/oem-counts';
import { routeForView } from '../../shared/oem-routes';
import type { RowFilter } from '../../shared/row-filter';
import { ALERTS } from '../../shared/seed/alerts';
import { CONNECTORS } from '../../shared/seed/connectors';
import { INCIDENTS } from '../../shared/seed/incidents';
import { CALC_NOTE } from '../../shared/source-calc';
import type { OemDomainData } from '../../shared/useOemData';
import type { MineMapPin } from '../../shared/viz/MineMap';
import { CONTRACTOR_ORG } from '../customers/CustomersScene.transform';

/** A heat row with its pin, so the table and the map cannot drift apart. */
export interface SitesOverviewRow extends SiteHeatRow {
  /** The site's worst machine band — what colours the pin. */
  readonly worstBand: ConditionBand;
  readonly pin: MineMapPin;
}

/**
 * Demo line 2049. `mapSVG`'s pin colour: the site takes the band of its worst
 * machine, `BORDER`-sorted (major first).
 */
export function worstBandAt(mine: MineCode): ConditionBand {
  const bands = FLEET.filter((f) => f.mine === mine).map((f) => statusOf(f.machine_id));
  return (
    bands.slice().sort((a, b) => CONDITION_BAND_ORDER[a] - CONDITION_BAND_ORDER[b])[0] ?? 'fair'
  );
}

/** Demo lines 2046 + 3151. The six sites, in `MINE_ORDER`. */
export function sitesOverview(): SitesOverviewRow[] {
  return siteHeat().map((row) => {
    const worstBand = worstBandAt(row.mine);
    const m = MINES[row.mine];
    const attention = row.bands
      .filter((b) => ['major', 'vpoor'].includes(b.band.key))
      .reduce((a, b) => a + b.n, 0);
    return {
      ...row,
      worstBand,
      pin: {
        mine: row.mine,
        name: row.name,
        lat: m.lat,
        lon: m.lon,
        machines: row.machines,
        colour: `var(--band-${worstBand})`,
        detail: `${row.machines} machines · ${attention} very poor or worse`,
      },
    };
  });
}

// ---------------------------------------------------------------------------
// Site cards — demo `.sitegrid`, line 2115
// ---------------------------------------------------------------------------

/** Demo line 2017. `drvOf` — battery first, then autonomous, else diesel. */
export type DriveKey = 'diesel' | 'batt' | 'auto';

/** Demo line 2016. `DRV` — the drive-type label the icon tooltip and the key print. */
export const DRIVE_LABEL: Readonly<Record<DriveKey, string>> = {
  diesel: 'diesel / diesel-electric',
  batt: 'battery-electric',
  auto: 'autonomous',
};

/** One machine icon on a site card — the demo's `mIcon(f)` (line 2020), data only. */
export interface SiteCardMachine {
  readonly id: string;
  readonly cls: MachineClass;
  readonly band: ConditionBand;
  readonly drive: DriveKey;
  /** `'L-02 · ML-18 · diesel / diesel-electric · Poor'` — the demo's `title`. */
  readonly title: string;
  readonly href: string;
}

/** A named working and the machines in it, e.g. `Northern · 9`. */
export interface SiteCardWorking {
  readonly label: string;
  readonly machines: number;
}

/** One site card, everything but availability (which needs the shift records). */
export interface SiteCard {
  readonly mine: MineCode;
  readonly name: string;
  readonly state: string;
  readonly dataMode: string;
  readonly isLive: boolean;
  /** First comma-part of the region, e.g. `'Gawler Ranges'`. */
  readonly region: string;
  readonly spec: readonly (readonly [label: string, value: string])[];
  /** Underground contractor at the site, or `null` where owner-operated. */
  readonly contractor: string | null;
  /** Empty at the sites with no named workings. */
  readonly workings: readonly SiteCardWorking[];
  readonly machines: readonly SiteCardMachine[];
  readonly veryPoorOrWorse: number;
  readonly poor: number;
  readonly href: string;
}

/**
 * Demo line 2119. The working names shortened the demo's way — the bracketed
 * aside, the word "workings" and the depth note all go, so a pill reads
 * `Northern 9` rather than `Northern workings (Spinifex) 9`.
 */
export const shortWorking = (working: string): string =>
  working.replace(/ \(.*\)/, '').replace(' workings', '').replace(' (below 500)', '');

const driveOf = (f: { battery: boolean; autonomous: boolean }): DriveKey =>
  f.battery ? 'batt' : f.autonomous ? 'auto' : 'diesel';

/** Demo lines 2115–2121. The six site cards, in `MINE_ORDER`. */
export function siteCards(): SiteCard[] {
  return MINE_ORDER.map((mine) => {
    const m = MINES[mine];
    const fs = FLEET.filter((f) => f.mine === mine);
    const bands = fs.map((f) => statusOf(f.machine_id));
    const wk = WORKINGS[mine];
    return {
      mine,
      name: m.name,
      state: m.state,
      dataMode: dataModeLabel(m),
      isLive: m.data_mode === 'Live',
      region: m.region.split(',')[0],
      spec: [
        ['Mine type', miningLabel(m)],
        ['Method', m.method],
        ['Commodities', m.commodity],
        ['Depth', `${groupNum(m.depth_m)} m`],
        ['Ownership', m.ownership],
      ],
      contractor: CONTRACTOR_ORG[mine] ?? null,
      workings: wk
        ? wk.map((w) => ({
            label: shortWorking(w),
            machines: fs.filter((f) => f.working === w).length,
          }))
        : [],
      machines: fs.map((f, i) => {
        const drive = driveOf(f);
        return {
          id: f.machine_id,
          cls: f.cls,
          band: bands[i],
          drive,
          title: `${f.machine_id} · ${f.model} · ${DRIVE_LABEL[drive]} · ${CONDITION_BAND_LABEL[bands[i]]}`,
          href: routeForView('machine', f.machine_id) ?? '/fleet',
        };
      }),
      veryPoorOrWorse: bands.filter((b) => b === 'major' || b === 'vpoor').length,
      poor: bands.filter((b) => b === 'poor').length,
      href: routeForView('sites', mine) ?? '/sites',
    };
  });
}

/** Demo line 2121. Mean availability per site for the week, `null` when unknown. */
export const siteCardAvailability = (ctx: ShiftCtx): Readonly<Record<MineCode, number | null>> =>
  Object.fromEntries(MINE_ORDER.map((mine) => [mine, siteAvail(mine, ctx)])) as Record<
    MineCode,
    number | null
  >;

// ---------------------------------------------------------------------------
// The eight summary measures — demo `.kpis.eight`, lines 2123–2126
// ---------------------------------------------------------------------------

/**
 * Demo line 2126. The "Connected systems" detail line is a literal in the
 * demo, not a join over `CONN`: it names systems by what they do for this
 * customer rather than by connector title. Kept as written.
 */
const CONNECTED_SYSTEMS_DETAIL =
  'Maximo (service register + Gawler Deep EAM) · SAP PM · Pitram · live MQTT · RMS · Salesforce · CatBase · SharePoint · bulletins · Maximo Mobile · XReach · Outlook · Teams · video · rig control · oil lab · tyres · LMS · roster · CSS';

/**
 * Demo line 2126. The ledger line count is a literal in the demo
 * (`B.cost_by_machine_cat.length*0+15083`) — the extract ships the ledger
 * summarised by machine and category, not line by line, so there is nothing to
 * count. Kept as the demo's figure.
 */
const LEDGER_LINES = 15083;

/** Demo line 2125. Why availability sits where it does — the demo's own note. */
const AVAILABILITY_NOTE =
  'Kalbarri lowest — reactive downtime; Mount Isolde includes a 6 h seismic exclusion; Gawler Deep carries the L-18 fire (43.5 h); Mount Read loses hours waiting for a charge bay';

/** The measures that need no fetch: machines, bands, connectors, open alerts. */
export interface EagerMeasures {
  readonly machines: number;
  readonly mines: number;
  /** `'30 Underground Drilling · 58 Underground Load & Haul · …'`. */
  readonly fleetMix: string;
  /** Good first, as the demo lists them. */
  readonly bands: readonly { readonly band: ConditionBandSpec; readonly n: number }[];
  readonly connectors: number;
  readonly connectorsDetail: string;
  readonly alerts: readonly { readonly id: string; readonly label: string; readonly href: string }[];
  readonly incidents: number;
}

/** Demo lines 2123, 2124, 2126 (connectors and alerts). */
export function eagerMeasures(): EagerMeasures {
  const inDivision = (d: string) => FLEET.filter((f) => f.division === d).length;
  return {
    machines: FLEET.length,
    mines: MINE_ORDER.length,
    fleetMix: [
      `${inDivision('Underground Drilling')} Underground Drilling`,
      `${inDivision('Load & Haul')} Underground Load & Haul`,
      `${inDivision('Surface Drilling')} Surface Drilling`,
      `${FLEET.filter((f) => f.battery).length} battery-electric`,
      `${FLEET.filter((f) => f.autonomous).length} autonomous`,
    ].join(' · '),
    bands: bandsBestFirst().map((band) => ({
      band,
      n: FLEET.filter((f) => statusOf(f.machine_id) === band.key).length,
    })),
    connectors: CONNECTORS.length,
    connectorsDetail: CONNECTED_SYSTEMS_DETAIL,
    alerts: ALERTS.map((a) => ({
      id: a.id,
      label: `${a.machine} ${a.short ?? a.comp.toLowerCase()}`,
      href: routeForView('alerts', a.id) ?? '/issues/alerts',
    })),
    incidents: INCIDENTS.length,
  };
}

/** The measures read off the shift records and the work orders. */
export interface WeekMeasures {
  /** Fleet availability for the reporting week, % of scheduled hours. */
  readonly availability: number;
  readonly availabilityBand: ConditionBandSpec;
  readonly availabilityNote: string;
  readonly drafts: number;
  /** `'WO-26-11889 · T-02 · WO-26-11901 · J-04 · …'`. */
  readonly draftList: string;
  /** Scope 1 + 2 for the reporting week, tonnes CO₂-e, rounded. */
  readonly emissionsT: number;
  /** What each figure opens in the source drawer — the demo's `FIG(...)`. */
  readonly sources: {
    readonly availability: MeasureSource;
    readonly drafts: MeasureSource;
    readonly emissions: MeasureSource;
  };
}

/** A measure's rows in the source drawer: dataset, title, filter and calc. */
export interface MeasureSource {
  readonly dataset: 'shift' | 'wos';
  readonly title: string;
  readonly filter?: RowFilter;
  readonly calc?: SourceCalc;
}

/** Demo lines 2125–2126. The week's shift records. */
const WEEK_FILTER: RowFilter = { date: { between: [WK.start, WK.end] } };

/** Demo line 2126. "Data in the store" opens every work order. */
export const STORE_SOURCE: MeasureSource = { dataset: 'wos', title: 'Work orders · all 18 months' };

/**
 * Demo lines 2112, 2125, 2126. Availability is `avgAvail` — note its divisor is
 * the whole fleet, not the machines that reported (see the engine). Emissions
 * are the week's `co2_kg` summed and divided by 1,000.
 */
export function weekMeasures(ctx: ShiftCtx & WorkOrderCtx): WeekMeasures {
  const availability = avgAvail(ctx);
  const drafts = draftWorkOrders(ctx);
  const week = ctx.shiftDaily.filter((s) => inWk(s.date));
  const co2kg = week.reduce((a, s) => a + s.co2_kg, 0);
  const reporting = FLEET.map((f) => availOf(f.machine_id, ctx)).filter(
    (v): v is number => v != null,
  );
  const availSum = reporting.reduce((a, v) => a + v, 0);
  return {
    availability,
    availabilityBand: bandOf(availability),
    availabilityNote: AVAILABILITY_NOTE,
    drafts: drafts.length,
    draftList: drafts.map((w) => `${w.wo_id} · ${w.machine_id}`).join(' · '),
    emissionsT: Math.round(co2kg / 1000),
    sources: {
      availability: {
        dataset: 'shift',
        title: `Shift records · ${WK.label}`,
        filter: WEEK_FILTER,
        calc: {
          formula: CALC_NOTE.fleetAvailability,
          steps: [
            `${reporting.length} of ${FLEET.length} machines have shift records this week`,
            `Fleet mean = ${availSum.toFixed(1)} ÷ ${FLEET.length} machines = ${availability}%`,
          ],
        },
      },
      drafts: {
        dataset: 'wos',
        title: 'Work orders · drafted by the agents',
        filter: { status: 'Draft - awaiting approval' },
      },
      emissions: {
        dataset: 'shift',
        title: `Shift records · ${WK.label}`,
        filter: WEEK_FILTER,
        calc: {
          formula: CALC_NOTE.fleetEmissions,
          steps: [
            `Σ co2_kg over ${week.length} machine-days = ${Math.round(co2kg).toLocaleString('en-AU')} kg`,
            `÷ 1,000 = ${Math.round(co2kg / 1000)} t CO₂-e`,
          ],
        },
      },
    },
  };
}

/** The payloads the "Data in the store" measure counts across. */
export interface DataStoreSources {
  readonly telemetry: OemDomainData['telemetry'];
  readonly workOrders: OemDomainData['work-orders'];
  readonly docIndex: OemDomainData['doc-index'];
  readonly parts: OemDomainData['parts'];
  readonly comms: OemDomainData['comms'];
}

/** Demo line 2126. "Data in the store". */
export interface DataStoreMeasure {
  readonly workOrders: number;
  /** `'1,100 machine-days of shift records · 3,008 alarm events · …'`. */
  readonly detail: string;
}

/**
 * Demo line 2126. Counts only — every figure is a row count of a payload the
 * page loads, except the ledger (a pinned literal, above) and the lake files,
 * which the browser cannot list and so come from the pinned count the nav
 * badge uses (`OEM_STATIC_COUNTS.data`, checked on every build).
 */
export function dataStoreMeasure(src: DataStoreSources): DataStoreMeasure {
  const alarmEvents = unpackTable<EventDailyRow>(src.telemetry.events_daily).reduce(
    (a, r) => a + r.n,
    0,
  );
  return {
    workOrders: src.workOrders.wos.rows.length,
    detail: [
      `${groupNum(src.telemetry.shift_daily.rows.length)} machine-days of shift records`,
      `${groupNum(alarmEvents)} alarm events`,
      `${groupNum(LEDGER_LINES)} ledger lines`,
      `${src.docIndex.docs.length + src.parts.bulletins.length} procedures, standards & bulletins`,
      `${OEM_STATIC_COUNTS.data} native-format files`,
      `${src.comms.emails.length} emails`,
      `${src.comms.meetings.length} meeting transcripts`,
    ].join(' · '),
  };
}
